import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FITS, type Fit, type Media } from '@seencast/shared';
import { usePlaylist, useSetPlaylistItems, useUpdatePlaylist } from '../../api/playlists.ts';
import { useMedia } from '../../api/media.ts';
import { usePermissions } from '../../auth/session.tsx';
import { Button, Loading, ErrorState } from '../../components/ui.tsx';
import { MediaThumb } from '../../components/MediaThumb.tsx';
import { EnginePreview } from '../../components/EnginePreview.tsx';
import { buildPlaylistManifest } from '../../lib/preview-manifest.ts';
import { formatDurationWords, titleCase, formatDuration } from '../../lib/format.ts';
import { ApiError } from '../../api/client.ts';

interface Row {
  key: string;
  media_id: string;
  duration_ms: number;
  fit: Fit;
}

function SortableRow({
  row,
  media,
  readOnly,
  onDuration,
  onFit,
  onRemove,
}: {
  row: Row;
  media: Media | undefined;
  readOnly: boolean;
  onDuration: (ms: number) => void;
  onFit: (fit: Fit) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.key,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} className={`playlist-item${isDragging ? ' dragging' : ''}`}>
      {!readOnly && (
        <span className="drag-handle" aria-label="Drag to reorder" {...attributes} {...listeners}>
          ⠿
        </span>
      )}
      <MediaThumb media={media ?? placeholderMedia(row.media_id)} className="picker-thumb" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="cell-strong" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {media?.filename ?? 'Unknown media'}
        </div>
        <div className="cell-dim" style={{ fontSize: 'var(--fs-xs)' }}>
          {media?.kind}
        </div>
      </div>
      <label className="sr-only" htmlFor={`dur-${row.key}`}>
        Duration seconds
      </label>
      <input
        id={`dur-${row.key}`}
        className="dur"
        type="number"
        min={1}
        step={1}
        disabled={readOnly}
        value={Math.round(row.duration_ms / 1000)}
        onChange={(e) => onDuration(Math.max(1, Number(e.target.value)) * 1000)}
        title="Seconds"
      />
      <select
        aria-label="Fit"
        disabled={readOnly}
        value={row.fit}
        onChange={(e) => onFit(e.target.value as Fit)}
        style={{ width: 'auto' }}
      >
        {FITS.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </select>
      {!readOnly && (
        <button className="close-x" aria-label="Remove item" onClick={onRemove}>
          ×
        </button>
      )}
    </div>
  );
}

function placeholderMedia(id: string): Media {
  return {
    id,
    org_id: '',
    kind: 'image',
    status: 'ready',
    filename: null,
    mime: null,
    size_bytes: null,
    width: null,
    height: null,
    duration_ms: null,
    src_url: null,
    created_at: 0,
  };
}

export function PlaylistEditor() {
  const { playlistId } = useParams({ from: '/playlists/$playlistId' });
  const navigate = useNavigate();
  const perms = usePermissions();
  const readOnly = !perms.canManageContent;

  const playlist = usePlaylist(playlistId);
  const media = useMedia();
  const setItems = useSetPlaylistItems();
  const updatePlaylist = useUpdatePlaylist();

  const [rows, setRows] = useState<Row[]>([]);
  const [name, setName] = useState('');
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');

  // Seed local state once the playlist loads.
  useEffect(() => {
    if (playlist.data) {
      setRows(
        playlist.data.items.map((it) => ({
          key: crypto.randomUUID(),
          media_id: it.media_id,
          duration_ms: it.duration_ms,
          fit: it.fit,
        })),
      );
      setName(playlist.data.name);
      setDirty(false);
    }
  }, [playlist.data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const mediaById = useMemo(() => {
    const map = new Map<string, Media>();
    (media.data ?? []).forEach((m) => map.set(m.id, m));
    return map;
  }, [media.data]);

  const totalMs = rows.reduce((acc, r) => acc + r.duration_ms, 0);

  // Live true-preview manifest. Pinned `now` keeps the single window's identity
  // stable across edits so the engine doesn't reset the loop to item 0 on every
  // keystroke. Memoised on the item SEQUENCE (media/duration/fit) + loaded media,
  // so typing in the name/search fields never churns the running preview.
  const previewNow = useRef(Date.now()).current;
  const seqKey = rows.map((r) => `${r.key}:${r.media_id}:${r.duration_ms}:${r.fit}`).join('|');
  const previewManifest = useMemo(
    // Recompute only when the item SEQUENCE (order/media/duration/fit) or loaded
    // media changes — `rows` is read fresh but keyed by seqKey so unrelated
    // re-renders (name/search typing) never churn the running preview.
    () => buildPlaylistManifest(rows, mediaById, previewNow),
    [seqKey, mediaById, previewNow],
  );

  if (playlist.isLoading) return <div className="page"><Loading /></div>;
  if (playlist.isError || !playlist.data)
    return <div className="page"><ErrorState message="Playlist not found." onRetry={() => playlist.refetch()} /></div>;

  function mutate(next: Row[]) {
    setRows(next);
    setDirty(true);
  }

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.key === active.id);
    const newIndex = rows.findIndex((r) => r.key === over.id);
    mutate(arrayMove(rows, oldIndex, newIndex));
  }

  function addMedia(m: Media) {
    mutate([
      ...rows,
      {
        key: crypto.randomUUID(),
        media_id: m.id,
        duration_ms: m.kind === 'video' ? m.duration_ms ?? 15000 : 10000,
        fit: 'contain',
      },
    ]);
  }

  function save() {
    setItems.mutate(
      { id: playlistId, body: { items: rows.map(({ media_id, duration_ms, fit }) => ({ media_id, duration_ms, fit })) } },
      {
        onSuccess: () => {
          toast.success('Playlist saved');
          setDirty(false);
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Save failed'),
      },
    );
  }

  function saveName() {
    if (name.trim() && name.trim() !== playlist.data!.name) {
      updatePlaylist.mutate({ id: playlistId, body: { name: name.trim() } });
    }
  }

  const pickerMedia = (media.data ?? []).filter((m) =>
    search ? (m.filename ?? '').toLowerCase().includes(search.toLowerCase()) : true,
  );

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ flex: 1 }}>
          <button className="btn ghost sm" onClick={() => navigate({ to: '/playlists' })}>
            ← Playlists
          </button>
          {readOnly ? (
            <h1 className="mt-2">{name}</h1>
          ) : (
            <input
              aria-label="Playlist name"
              className="mt-2"
              style={{ fontSize: 'var(--fs-xl)', fontWeight: 650, maxWidth: 420 }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
            />
          )}
        </div>
        {!readOnly && (
          <div className="row">
            {dirty && <span className="text-dim">Unsaved changes</span>}
            <Button variant="primary" onClick={save} disabled={!dirty || setItems.isPending}>
              {setItems.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      <div className="editor-cols">
        {/* Media picker */}
        {!readOnly && (
          <div className="card card-pad">
            <div className="card-title">Add media</div>
            <input
              className="mb-4"
              aria-label="Search media to add"
              placeholder="Search media…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div style={{ maxHeight: 460, overflow: 'auto' }}>
              {pickerMedia.length === 0 ? (
                <p className="text-dim">No media. Upload some on the Media page.</p>
              ) : (
                pickerMedia.map((m) => (
                  <div key={m.id} className="picker-item" onClick={() => addMedia(m)}>
                    <MediaThumb media={m} className="picker-thumb" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="cell-strong" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {m.filename}
                      </div>
                      <div className="cell-dim" style={{ fontSize: 'var(--fs-xs)' }}>
                        {titleCase(m.kind)}
                      </div>
                    </div>
                    <span className="text-dim">+</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Ordered items */}
        <div className="card card-pad">
          <div className="card-title">
            Items ({rows.length}) · loop {formatDurationWords(totalMs)}
          </div>
          {rows.length === 0 ? (
            <p className="text-dim">This playlist is empty. Add media from the left.</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={rows.map((r) => r.key)} strategy={verticalListSortingStrategy}>
                {rows.map((row) => (
                  <SortableRow
                    key={row.key}
                    row={row}
                    media={mediaById.get(row.media_id)}
                    readOnly={readOnly}
                    onDuration={(ms) => mutate(rows.map((r) => (r.key === row.key ? { ...r, duration_ms: ms } : r)))}
                    onFit={(fit) => mutate(rows.map((r) => (r.key === row.key ? { ...r, fit } : r)))}
                    onRemove={() => mutate(rows.filter((r) => r.key !== row.key))}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Preview */}
        <div className="card card-pad">
          <div className="card-title">Preview</div>
          <EnginePreview
            manifest={previewManifest}
            emptyLabel="Add media to preview the loop"
            ariaLabel="Playlist preview"
          />
          <div className="dl mt-4">
            <dt>Items</dt>
            <dd>{rows.length}</dd>
            <dt>Loop length</dt>
            <dd>{formatDuration(totalMs)}</dd>
          </div>
        </div>
      </div>
    </div>
  );
}
